import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import { FormikProps } from 'formik'

import Form from '../../../../shared/components/Form/Form.component'

import { FormField, FormSection } from '../../../../shared/components/Form/Form.types'
import { RecipientDetailsValues } from '../../CreateFulfillmentOrder.types'
import {
  INITIAL_RECIPIENT_VALUES,
  US_ZIP_PLUS_FOUR_REGEX,
  ZIP_PLUS_FOUR_ENFORCED_HINT,
  ZIP_PLUS_FOUR_HINT,
  buildRecipientDetailsValidationSchema
} from './RecipientDetails.dictionary'
import { RecipientDetailsProps } from './RecipientDetails.types'

const RecipientDetails: React.FC<RecipientDetailsProps> = (props) => {
  const {
    onFormChange,
    countriesAndCities,
    initialValues,
    validateOnMount,
    addressFieldsReadonly = false,
    additionalContent,
    enforceZipPlus4 = false,
    suggestedZipPlus4 = null
  } = props

  const formikRef = useRef<FormikProps<RecipientDetailsValues> | null>(null)

  const validationSchema = useMemo(() => buildRecipientDetailsValidationSchema({ enforceZipPlus4 }), [enforceZipPlus4])

  const countryOptions = useMemo(() => {
    if (!countriesAndCities) return []

    return countriesAndCities.map((country) => ({
      value: country.iataCountryCode,
      label: country.countryName !== 'Unknown' ? country.countryName : country.iataCountryCode
    }))
  }, [countriesAndCities])

  const formSections = useMemo((): FormSection[] => {
    const personalFields: FormField[] = [
      {
        name: 'firstName',
        label: 'First Name',
        type: 'text',
        required: true,
        fullWidth: true,
        grid: { xs: 12, sm: 6 }
      },
      {
        name: 'lastName',
        label: 'Last Name',
        type: 'text',
        required: true,
        fullWidth: true,
        grid: { xs: 12, sm: 6 }
      },
      {
        name: 'email',
        label: 'Email Address',
        type: 'email',
        required: true,
        fullWidth: true,
        grid: { xs: 12, sm: 6 }
      },
      {
        name: 'phone',
        label: 'Phone Number',
        type: 'tel',
        required: false,
        fullWidth: true,
        grid: { xs: 12, sm: 6 }
      }
    ]

    const addressFields: FormField[] = [
      {
        name: 'streetAddress',
        label: 'Street Address',
        type: 'text',
        required: true,
        fullWidth: true,
        grid: { xs: 12 },
        readonly: addressFieldsReadonly
      },
      {
        name: 'country',
        label: 'Country',
        type: 'select',
        required: true,
        fullWidth: true,
        grid: { xs: 12, sm: 6 },
        options: countryOptions,
        readonly: addressFieldsReadonly
      },
      {
        name: 'city',
        label: 'City',
        type: 'text',
        required: true,
        fullWidth: true,
        grid: { xs: 12, sm: 6 },
        readonly: addressFieldsReadonly
      },
      {
        name: 'stateProvince',
        label: 'State/Province',
        type: 'text',
        required: true,
        fullWidth: true,
        grid: { xs: 12, sm: 6 },
        readonly: addressFieldsReadonly
      },
      {
        name: 'postalCode',
        label: 'Postal Code',
        type: 'text',
        required: true,
        fullWidth: true,
        grid: { xs: 12, sm: 6 },
        readonly: addressFieldsReadonly,
        helperText: enforceZipPlus4 ? ZIP_PLUS_FOUR_ENFORCED_HINT : ZIP_PLUS_FOUR_HINT
      },
      {
        name: 'deliveryInstructions',
        label: 'Delivery Instructions (optional)',
        type: 'textarea',
        fullWidth: true,
        grid: { xs: 12 },
        rows: 3,
        placeholder: 'Enter any special delivery instructions...',
        readonly: addressFieldsReadonly
      }
    ]

    return [
      {
        title: 'Personal Information',
        fields: personalFields
      },
      {
        id: 'address-details-form',
        title: 'Address Details',
        fields: addressFields
      }
    ]
  }, [countryOptions, addressFieldsReadonly, enforceZipPlus4])

  const handleFormChange = useCallback(
    (formikPartial: Partial<FormikProps<unknown>>) => {
      const partial = formikPartial as Partial<FormikProps<RecipientDetailsValues>>
      formikRef.current = partial as FormikProps<RecipientDetailsValues>

      if (partial.values) {
        try {
          validationSchema.validateSync(partial.values)
          onFormChange(true, partial.values as RecipientDetailsValues)
        } catch {
          onFormChange(false, partial.values as RecipientDetailsValues)
        }
      }
    },
    [onFormChange, validationSchema]
  )

  useEffect(() => {
    if (!suggestedZipPlus4) return

    const formik = formikRef.current
    if (!formik) return

    const current = formik.values.postalCode ?? ''
    if (US_ZIP_PLUS_FOUR_REGEX.test(current)) return

    const fiveDigit = current.replace(/\D/g, '').slice(0, 5)
    if (fiveDigit.length !== 5) return

    const plus = suggestedZipPlus4.replace(/\D/g, '').slice(0, 4)
    if (plus.length !== 4) return

    const next = `${fiveDigit}-${plus}`
    if (next !== current) {
      formik.setFieldValue('postalCode', next)
    }
  }, [suggestedZipPlus4])

  const resolvedInitialValues = initialValues ?? INITIAL_RECIPIENT_VALUES

  return (
    <Form
      headerId="recipient-details-title"
      title="Recipient Details"
      subtitle="Provide the delivery details for the recipient."
      initialValues={resolvedInitialValues}
      validationSchema={validationSchema}
      onSubmit={() => undefined}
      onFormChange={handleFormChange}
      formSections={formSections}
      hideButtons
      enableReinitialize
      validateOnMount={validateOnMount ?? false}
      isContained
      customElement={additionalContent}
    />
  )
}

export default RecipientDetails
